// Adults on file: guardians, authorized adults and delegates. Edit contact details, photo and
// notes; see which children each is linked to, and the parent's consent to exception texts.
import { useEffect, useMemo, useState } from "react";
import { Badge, Banner, Button, PhotoTile, Sheet, Table, TextField } from "@vavr/ui";
import { formatUsPhone } from "@vavr/core";
import { api, dateTime, fullName, Link, useQuery } from "../shared/index.ts";
import { useConsole, useGet, useAction, useBlob } from "./data.tsx";
import { FileButton, KIND_LABEL, LoadError, Loading, Toolbar, uploadPhoto, validity } from "./common.tsx";

interface AdultRow { id: string; first: string; last: string; phone: string | null; email: string | null; notes: string | null; photo_sha256: string | null; children: number }

export function Adults() {
  const { base } = useConsole();
  const adults = useGet<{ adults: AdultRow[] }>(`${base}/adults`);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  // Global search opens an adult here with ?open=<id>.
  const wanted = new URLSearchParams(useQuery()).get("open");
  useEffect(() => { if (wanted) setOpen(wanted); }, [wanted]);
  const rows = useMemo(() => (adults.data?.adults ?? []).filter((a) => !q || `${a.first} ${a.last} ${a.email ?? ""} ${a.phone ?? ""}`.toLowerCase().includes(q.trim().toLowerCase())), [adults.data, q]);
  if (adults.loading) return <Loading />;
  if (adults.error) return <LoadError error={adults.error} retry={adults.reload} />;
  return (
    <div className="pmu-stack">
      <Toolbar>
        <TextField label="Search" hideLabel type="search" placeholder="Search by name, email or phone  ( / )" value={q} onChange={(e) => setQ(e.target.value)} data-search="" />
      </Toolbar>
      <Table
        caption={`${rows.length} ${rows.length === 1 ? "adult" : "adults"}`} rowKey={(a) => a.id} rows={rows} maxHeight="calc(100dvh - 220px)"
        empty="No adults yet. They are added from a child's page or by importing a roster."
        columns={[
          { key: "name", header: "Name", rowHeader: true, render: (a) => <button type="button" className="pmu-c-linkbutton" onClick={() => setOpen(a.id)}>{a.last}, {a.first}</button> },
          { key: "email", header: "Email", render: (a) => a.email ?? "" },
          { key: "phone", header: "Phone", render: (a) => a.phone ?? "" },
          { key: "children", header: "Children", align: "end", render: (a) => (a.children ? a.children : <Badge tone="warning">None</Badge>) },
        ]}
      />
      <Sheet title="Adult" open={open !== null} onOpenChange={(o) => !o && setOpen(null)}>
        {open && <AdultPanel id={open} />}
      </Sheet>
    </div>
  );
}

function AdultPanel({ id }: { id: string }) {
  const { base, site } = useConsole();
  const d = useGet<{ adult: AdultRow & { has_pin: number }; children: any[]; restrictions: any[] }>(`${base}/adults/${id}`);
  const [f, setF] = useState<{ first: string; last: string; phone: string; email: string; notes: string } | null>(null);
  const photo = useBlob(base, d.data?.adult.photo_sha256);
  const act = useAction();
  if (d.loading) return <Loading />;
  if (d.error || !d.data) return <LoadError error={d.error ?? new Error("Not found")} />;
  const a = d.data.adult;
  const form = f ?? { first: a.first, last: a.last, phone: a.phone ?? "", email: a.email ?? "", notes: a.notes ?? "" };
  const body = (over: Record<string, unknown> = {}) => ({ first: form.first, last: form.last, phone: form.phone || null, email: form.email || null, notes: form.notes || null, photo_sha256: null, ...over });
  const invalidate = [`${base}/adults`];
  return (
    <div className="pmu-stack">
      {act.error && <Banner tone="danger" title={act.error} />}
      {d.data.restrictions.length > 0 && <Banner tone="restriction" title={`Named in ${d.data.restrictions.length} restriction${d.data.restrictions.length > 1 ? "s" : ""}`}>See the child's page for details.</Banner>}
      <div className="pmu-row">
        <PhotoTile name={fullName(a)} src={photo} detail={a.has_pin ? "Has a door PIN" : undefined} />
        <FileButton label={a.photo_sha256 ? "Replace photo" : "Add a photo"} accept="image/*" disabled={act.busy}
          onFile={(file) => act.run(async () => { const sha = await uploadPhoto(base, file); await api("PUT", `${base}/adults/${a.id}`, body({ photo_sha256: sha })); }, { success: "Photo saved.", invalidate })} />
      </div>
      <form className="pmu-stack" onSubmit={async (e) => { e.preventDefault(); await act.run(() => api("PUT", `${base}/adults/${a.id}`, body()), { success: "Saved.", invalidate }); }}>
        <div className="pmu-grid-2">
          <TextField label="First name" required value={form.first} onChange={(e) => setF({ ...form, first: e.target.value })} />
          <TextField label="Last name" required value={form.last} onChange={(e) => setF({ ...form, last: e.target.value })} />
          <TextField label="Mobile phone" type="tel" value={form.phone} onChange={(e) => setF({ ...form, phone: e.target.value })} />
          <TextField label="Email" type="email" value={form.email} onChange={(e) => setF({ ...form, email: e.target.value })} />
        </div>
        <TextField label="Notes for staff" value={form.notes} onChange={(e) => setF({ ...form, notes: e.target.value })} />
        <div><Button type="submit" loading={act.busy} disabled={!form.first.trim() || !form.last.trim()}>Save</Button></div>
      </form>
      <ExceptionTexts base={base} adultId={a.id} tz={site.tz} />
      <h3 className="pmu-c-h3">Linked children</h3>
      {d.data.children.length === 0 ? <p className="pmu-muted">Not linked to any child.</p> : (
        <ul className="pmu-list">
          {d.data.children.map((c) => {
            const v = validity(c.valid_from, c.valid_to, site.tz);
            return (
              <li key={c.id} className="pmu-row pmu-row--between">
                <Link className="pmu-link" href={`/console/children/${c.id}`}>{fullName(c)}</Link>
                <span className="pmu-row"><span className="pmu-small">{c.relation}</span><Badge tone={c.kind === "guardian" ? "brand" : "neutral"}>{KIND_LABEL[c.kind] ?? c.kind}</Badge><Badge tone={v.tone}>{v.label}</Badge></span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

interface SmsAccount { account_id: string; name: string; phone: string | null; consent: { granted: boolean; source: string; ts: number } | null }

const HOW: Record<string, string> = { app: "in the app", office: "recorded by the office", sms: "by replying to a text" };

/**
 * Exception texts (PMU-163) go to the number on the parent's own account, not the roster number
 * above; this shows each linked account's consent and lets the office record consent given on
 * paper or a "stop" said in person.
 */
function ExceptionTexts({ base, adultId, tz }: { base: string; adultId: string; tz?: string }) {
  const key = `${base}/adults/${adultId}/sms`;
  const d = useGet<{ enabled: boolean; accounts: SmsAccount[] }>(key);
  const act = useAction();
  if (!d.data?.enabled || !d.data.accounts.length) return null;
  return (
    <>
      <h3 className="pmu-c-h3">Exception texts</h3>
      <ul className="pmu-list">
        {d.data.accounts.map((x) => {
          const c = x.consent;
          const how = c ? HOW[c.source.split(":")[0]!] ?? c.source : null;
          const record = (granted: boolean) => act.run(() => api("POST", key, { phone: x.phone, granted }), { success: granted ? "Consent recorded." : "Texts stopped.", invalidate: [key] });
          return (
            <li key={x.account_id} className="pmu-row pmu-row--between">
              <span className="pmu-stack pmu-stack--tight">
                <span>{x.phone ? formatUsPhone(x.phone) : `${x.name} has not added a mobile number`}</span>
                {x.phone && <span className="pmu-small pmu-muted">{c ? `${c.granted ? "On" : "Off"} since ${dateTime(c.ts, tz)}, ${how}` : "Not asked yet"}</span>}
              </span>
              {x.phone && (
                <span className="pmu-row">
                  <Badge tone={c?.granted ? "success" : "neutral"}>{c?.granted ? "Texts on" : "Texts off"}</Badge>
                  {c?.granted
                    ? <Button size="sm" variant="ghost" disabled={act.busy} onClick={() => record(false)}>They asked to stop</Button>
                    : <Button size="sm" variant="secondary" disabled={act.busy} onClick={() => record(true)}>Record consent on paper</Button>}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </>
  );
}
